import { WindowPostMessageProxy, IMessageHandler } from 'window-post-message-proxy';
import * as m from './messages';

interface ICallbackItem {
    [key: string]: Function;
}

export class MessengerService implements IMessageHandler {
    private readonly proxy: WindowPostMessageProxy;
    private callbackQueue: ICallbackItem = {};
    public readonly isEnabled: boolean;
    private _isConnected: boolean = false;
    private clientId: string = '';
    private frameId: string = '';
    private onInit?: () => void;

    constructor(private readonly window: Window, handlers?: m.IInitHandlers) {
        this.isEnabled = this.window !== this.window.parent && !this.window.opener;
        this.proxy = new WindowPostMessageProxy({ suppressWarnings: true });
        this.onInit = handlers?.onInit;

        if (this.isEnabled) {
            this.proxy.addHandler(this);
            this.proxy.start();

            if (typeof handlers?.relayHandlers !== 'undefined') {
                this.registerRelayHandlers(handlers.relayHandlers);
            }
        }
    }

    /**
     * Indicates whether the plugin is connected to the main application.
     * For reading information from the app regarding, project, user or
     * subscriptions this needs to be true
     */
    get isConnected(): boolean {
        return this._isConnected;
    }

    /**
     * Test if service is active
     * @param message
     * @returns Boolean
     */
    test(message: any): boolean {
        if (message && message.type) {
            switch (message.type) {
                case m.INIT:
                case m.CALLBACK:
                case m.RELAY_FORWARD:
                    return true;
                default:
                    return false;
            }
        }
        return false;
    }

    /**
     * Register callbacks for relay messages
     * @param payload Array of objects with type, callback properties
     */
    registerRelayHandlers(payload: m.IRelayHandler[]) {
        payload.forEach(({ type, callback }) => {
            this.callbackQueue[type] = callback;
        });
    }

    /**
     * Create object and add item to project
     * @param payload Payload to add to project
     * @param callback Trigger when operation done
     */
    create(payload: m.ICreate[], callback?: (data: any) => void) {
        let data = new m.SetCreateMessage(payload);
        // add callback method if provided
        if (callback) {
            this.callbackQueue[data.requestId] = callback;
        }
        this.send(data);
    }

    /**
     * Create drag start and add item to project
     * @param payload Payload to add to project
     * @param callback Trigger when operation done
     */
    dragStart(payload: m.ICreate[], callback?: (data: any) => void) {
        let data = new m.SetDragStartMessage(payload);
        // add callback method if provided
        if (callback) {
            this.callbackQueue[data.requestId] = callback;
        }
        this.send(data);
    }

    /**
     * Send Drag end signal
     * @param payload Payload to add to project
     * @param callback Trigger when operation done
     */
    dragEnd(payload: m.IMouse, callback?: (data: any) => void) {
        let data = new m.SetDragEndMessage(payload);
        // add callback method if provided
        if (callback) {
            this.callbackQueue[data.requestId] = callback;
        }
        this.send(data);
    }

    read(scopes: string[], callback?: (data: any) => void) {
        let data = new m.SetReadMessage(scopes);
        // add callback method if provided
        if (callback) {
            this.callbackQueue[data.requestId] = callback;
        }
        this.send(data);
    }

    subscribePlugin(productId: string, callback?: (data: any) => void) {
        let data = new m.SetSubscribePluginMessage(productId);
        // add callback method if provided
        if (callback) {
            this.callbackQueue[data.requestId] = callback;
        }
        this.send(data);
    }

    /**
     * Create object and add item to project
     * @param payload Payload to add to project
     * @param callback Trigger when operation done
     */
    invokePlugin(component: string, callback?: (data: any) => void) {
        let data = new m.SetInvokeMessage({
            component,
        });
        // add callback method if provided
        if (callback) {
            this.callbackQueue[data.requestId] = callback;
        }
        this.send(data);
    }

    relayMessage(targetId: string, message: any, callback?: (data: any) => void) {
        let data = new m.SetRelayMessage({
            targetId,
            message,
        });
        // add callback method if provided
        if (callback) {
            this.callbackQueue[data.requestId] = callback;
        }
        this.send(data);
    }

    update() {
        return 'To be implemented';
    }

    delete() {
        return 'To be implemented';
    }

    /**
     * Trigger callback method when response received from parent
     * @param callback
     */
    private callback(message: m.CallbackMessage) {
        let requestId = message.requestId;
        if (this.callbackQueue[requestId]) {
            if (message.success) {
                this.callbackQueue[requestId](message.data, null);
            } else {
                this.callbackQueue[requestId](null, message.data);
            }
            delete this.callbackQueue[requestId];
        }
    }

    /**
     * Trigger handler when a relay message received
     * @param message
     */
    private relay(message: m.RelayMessage) {
        const { payload } = message;
        const { type, data } = payload;

        if (type) {
            const msg: m.CallbackMessage = {
                type: m.CALLBACK,
                requestId: type,
                data,
                success: true,
            };
            this.callback(msg);
        }
    }

    /**
     * Receive messages from parent
     * @param message
     */
    handle(message: Readonly<m.IMessage>) {
        switch (message.type) {
            case m.INIT:
                const data = message as m.InitMessage;
                this.clientId = data.clientId;
                sessionStorage.setItem('pw_clid', data.clientId);
                this.frameId = data.frameId;
                sessionStorage.setItem('pw_fid', data.frameId);
                this._isConnected = true;

                if (typeof this.onInit === 'function') {
                    this.onInit();
                }
                break;
            case m.CALLBACK:
                this.callback(message as m.CallbackMessage);
                break;
            case m.RELAY_FORWARD:
                this.relay(message as m.RelayMessage);
                break;
        }
        return { success: true };
    }

    /**
     * Send message to parent
     * @param message
     */
    send(message: m.IMessage): void {
        const payload = {
            clientId: this.clientId || sessionStorage.getItem('pw_clid'),
            frameId: this.frameId || sessionStorage.getItem('pw_fid'),
            ...message,
        };

        this.proxy.postMessage(this.window.parent as unknown as Window, payload);

        if (window.vlogr) {
            window.vlogr.addData(1, JSON.stringify(payload));
        }
        if (window.webkit) {
            window.webkit.messageHandlers.addData.postMessage(payload);
        }
    }
}
